<template>
  <div class="ledger-page">
    <b-card
      v-if="canCreate"
      class="rounded-lg ledger-opening-wizard"
    >
      <h3 class="mb-1">
        {{ $t('ledger_opening_title') }}
      </h3>
      <p class="text-muted">
        {{ $t('ledger_opening_intro') }}
      </p>

      <div class="d-none d-md-block">
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <div class="ledger-step-label">
              1
            </div>
            <b-form-group :label="$t('ledger_opening_as_of')">
              <b-form-input
                v-model="form.as_of"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <div class="ledger-step-label">
              2
            </div>
            <b-form-group
              :label="$t('ledger_opening_equity')"
              :description="$t('ledger_opening_equity_help')"
            >
              <b-form-input
                v-model="form.equity_code"
                maxlength="20"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
            class="d-flex align-items-end mb-1"
          >
            <b-button
              variant="primary"
              class="rounded-pill"
              :disabled="posting || !form.as_of"
              @click="confirmCreate"
            >
              <b-spinner
                v-if="posting"
                small
                class="mr-50"
              />
              {{ $t('ledger_opening_post') }}
            </b-button>
          </b-col>
        </b-row>
        <b-button
          variant="link"
          class="px-0"
          @click="showAdvanced = !showAdvanced"
        >
          {{ $t('ledger_opening_advanced') }}
        </b-button>
        <b-collapse v-model="showAdvanced">
          <b-form-group
            :label="$t('ledger_opening_map')"
            :description="$t('ledger_opening_map_help')"
            class="mb-0"
            label-for="map-amount-desktop"
          >
            <b-form-input
              id="map-amount-desktop"
              v-model.number="form.map_inventory_amount"
              type="number"
              min="0"
              step="0.01"
            />
          </b-form-group>
        </b-collapse>
      </div>

      <div class="d-md-none">
        <div v-show="step === 1">
          <div class="ledger-step-label">
            1 · {{ $t('ledger_opening_step_date') }}
          </div>
          <b-form-group :label="$t('ledger_opening_as_of')">
            <b-form-input
              v-model="form.as_of"
              type="date"
            />
          </b-form-group>
          <b-button
            variant="primary"
            block
            class="rounded-pill"
            :disabled="!form.as_of"
            @click="step = 2"
          >
            {{ $t('ledger_opening_next') }}
          </b-button>
        </div>

        <div v-show="step === 2">
          <div class="ledger-step-label">
            2 · {{ $t('ledger_opening_step_equity') }}
          </div>
          <b-form-group
            :label="$t('ledger_opening_equity')"
            :description="$t('ledger_opening_equity_help')"
          >
            <b-form-input
              v-model="form.equity_code"
              maxlength="20"
            />
          </b-form-group>
          <div class="d-flex">
            <b-button
              variant="outline-secondary"
              class="rounded-pill mr-50"
              @click="step = 1"
            >
              {{ $t('ledger_opening_back') }}
            </b-button>
            <b-button
              variant="primary"
              class="rounded-pill flex-grow-1"
              @click="step = 3"
            >
              {{ $t('ledger_opening_next') }}
            </b-button>
          </div>
        </div>

        <div v-show="step === 3">
          <div class="ledger-step-label">
            3 · {{ $t('ledger_opening_step_map') }}
          </div>
          <b-button
            variant="link"
            class="px-0"
            @click="showAdvanced = !showAdvanced"
          >
            {{ $t('ledger_opening_advanced') }}
          </b-button>
          <b-collapse v-model="showAdvanced">
            <b-form-group
              :label="$t('ledger_opening_map')"
              :description="$t('ledger_opening_map_help')"
              label-for="map-amount-mobile"
            >
              <b-form-input
                id="map-amount-mobile"
                v-model.number="form.map_inventory_amount"
                type="number"
                min="0"
                step="0.01"
              />
            </b-form-group>
          </b-collapse>
          <p class="text-muted small">
            {{ $t('ledger_opening_map_closed') }}
          </p>
          <div class="d-flex">
            <b-button
              variant="outline-secondary"
              class="rounded-pill mr-50"
              @click="step = 2"
            >
              {{ $t('ledger_opening_back') }}
            </b-button>
            <b-button
              variant="primary"
              class="rounded-pill flex-grow-1"
              :disabled="posting || !form.as_of"
              @click="confirmCreate"
            >
              {{ $t('ledger_opening_post') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-card>

    <b-alert
      v-if="replay"
      show
      variant="success"
    >
      <div class="font-weight-bold mb-50">
        {{ $t('ledger_opening_already') }}
      </div>
      <div class="mb-50">
        <strong class="ledger-num">{{ replay.as_of }}</strong>
        · {{ entryNumber(replay) }}
        <b-badge
          :variant="statusVariant(replay)"
          class="ml-50"
        >
          {{ statusText(replay) }}
        </b-badge>
      </div>
      <div class="ledger-num mb-50">
        {{ $t('ledger_debit') }} {{ money(totalsOf(replay).debit) }}
        ·
        {{ $t('ledger_credit') }} {{ money(totalsOf(replay).credit) }}
      </div>
      <b-link
        v-if="canOpenJournal && journalId(replay)"
        :to="journalRoute(replay)"
      >
        {{ $t('ledger_journal_open') }}
      </b-link>
    </b-alert>

    <b-card class="rounded-lg">
      <h4 class="mb-1">
        {{ $t('ledger_opening_runs') }}
      </h4>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!runs.length"
        class="text-center py-4"
      >
        <p class="text-muted mb-1">
          {{ $t('ledger_opening_empty') }}
        </p>
        <p
          v-if="canCreate"
          class="text-muted small mb-0"
        >
          {{ $t('ledger_opening_empty_cta') }}
        </p>
      </div>

      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="runs"
            :fields="fields"
            :tbody-tr-class="rowClass"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(as_of)="data">
              <span class="ledger-num">{{ data.item.as_of }}</span>
            </template>
            <template #cell(entry_number)="data">
              <b-link
                v-if="journalId(data.item) && canOpenJournal"
                :to="journalRoute(data.item)"
              >
                {{ entryNumber(data.item) }}
              </b-link>
              <span v-else>{{ entryNumber(data.item) }}</span>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="statusVariant(data.item)">
                {{ statusText(data.item) }}
              </b-badge>
            </template>
            <template #cell(debit)="data">
              <span class="ledger-num">{{ money(totalsOf(data.item).debit) }}</span>
            </template>
            <template #cell(credit)="data">
              <span class="ledger-num">{{ money(totalsOf(data.item).credit) }}</span>
            </template>
          </b-table>
        </div>

        <div class="d-md-none">
          <div
            v-for="run in runs"
            :key="run.id"
            class="ledger-opening-card"
            :class="{ 'is-replay': replay && replay.id === run.id }"
          >
            <div class="d-flex justify-content-between align-items-center mb-50">
              <strong class="ledger-num">{{ run.as_of }}</strong>
              <b-badge :variant="statusVariant(run)">
                {{ statusText(run) }}
              </b-badge>
            </div>
            <div>
              <b-link
                v-if="journalId(run) && canOpenJournal"
                :to="journalRoute(run)"
              >
                {{ entryNumber(run) }}
              </b-link>
              <span v-else>{{ entryNumber(run) }}</span>
            </div>
            <div class="ledger-num text-muted mt-50">
              {{ $t('ledger_debit') }} {{ money(totalsOf(run).debit) }}
              ·
              {{ $t('ledger_credit') }} {{ money(totalsOf(run).credit) }}
            </div>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BButton, BCollapse, BTable, BBadge, BLink, BAlert, BSpinner,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getOpeningBalances, createOpeningBalance } from '@/api/accounting/openingBalances'
import {
  listFrom, oneFrom, formatLedgerMoney, roundMoney, todayIso, enumValue, enumLabel,
  statusBadgeVariant, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'OpeningBalances',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BCollapse,
    BTable,
    BBadge,
    BLink,
    BAlert,
    BSpinner,
  },
  data() {
    return {
      loading: false,
      posting: false,
      showAdvanced: false,
      step: 1,
      runs: [],
      replay: null,
      form: {
        as_of: todayIso(),
        equity_code: '3130',
        map_inventory_amount: 0,
      },
    }
  },
  computed: {
    canCreate() {
      return this.hasPermission('create opening_balances')
    },
    canOpenJournal() {
      return this.hasPermission('view journal_entries')
    },
    fields() {
      return [
        { key: 'as_of', label: this.$t('ledger_opening_as_of') },
        { key: 'entry_number', label: this.$t('ledger_entry_number') },
        { key: 'status', label: this.$t('ledger_status') },
        { key: 'debit', label: this.$t('ledger_debit') },
        { key: 'credit', label: this.$t('ledger_credit') },
      ]
    },
  },
  mounted() {
    this.loadRuns()
  },
  methods: {
    money(value) {
      if (value == null || value === '') return '—'
      return formatLedgerMoney(value, this.$i18n.locale)
    },
    journalOf(run) {
      return (run && run.journal_entry) || null
    },
    journalId(run) {
      const journal = this.journalOf(run)
      return (journal && journal.id) || (run && run.journal_entry_id) || null
    },
    entryNumber(run) {
      const journal = this.journalOf(run)
      return (journal && journal.entry_number) || '—'
    },
    statusText(run) {
      const journal = this.journalOf(run)
      return enumLabel(journal && journal.status, this.$i18n.locale) || this.$t('ledger_status_posted')
    },
    statusVariant(run) {
      const journal = this.journalOf(run)
      return statusBadgeVariant(enumValue(journal && journal.status) || 'posted')
    },
    totalsOf(run) {
      const journal = this.journalOf(run)
      const totals = (journal && journal.totals) || {}
      return {
        debit: totals.debit,
        credit: totals.credit,
      }
    },
    journalRoute(run) {
      return { name: 'ledger-journals-view', params: { id: this.journalId(run) } }
    },
    rowClass(item) {
      if (this.replay && item && item.id === this.replay.id) return 'table-success'
      return ''
    },
    async loadRuns() {
      this.loading = true
      try {
        const res = await getOpeningBalances()
        this.runs = listFrom(res).slice().sort((a, b) => {
          const byDate = String(b.as_of || '').localeCompare(String(a.as_of || ''))
          if (byDate !== 0) return byDate
          return (Number(b.id) || 0) - (Number(a.id) || 0)
        })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async confirmCreate() {
      if (!this.canCreate || this.posting) return
      if (!this.form.as_of) return
      const amount = Number(this.form.map_inventory_amount)
      if (!Number.isFinite(amount) || amount < 0) {
        this.toast(this.$t('ledger_opening_map_invalid'), 'warning')
        return
      }
      const result = await Swal.fire({
        text: this.$t('ledger_opening_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_opening_post'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      await this.createRun()
    },
    async createRun() {
      this.posting = true
      this.replay = null
      const equity = String(this.form.equity_code || '').trim() || '3130'
      const payload = {
        as_of: this.form.as_of,
        equity_code: equity,
        map_inventory_amount: roundMoney(this.form.map_inventory_amount || 0),
      }
      try {
        const res = await createOpeningBalance(payload)
        const status = res && res.status
        const body = status ? res.data : res
        const run = oneFrom(body)
        if (status === 200) {
          this.replay = run
          this.toast(this.$t('ledger_opening_already'), 'info')
          await this.loadRuns()
          return
        }
        this.toast(this.$t('ledger_opening_success'), 'success')
        const journalId = this.journalId(run)
        if (journalId && this.canOpenJournal) {
          this.$router.push({ name: 'ledger-journals-view', params: { id: journalId } })
          return
        }
        await this.loadRuns()
      } catch (error) {
        const status = error?.response?.status
        if (status === 422) {
          this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'warning')
          return
        }
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.posting = false
      }
    },
    toast(title, variant) {
      const icons = {
        success: 'CheckIcon',
        info: 'InfoIcon',
        warning: 'AlertTriangleIcon',
        danger: 'AlertCircleIcon',
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: icons[variant] || 'InfoIcon',
          variant: variant === 'info' ? 'info' : variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-step-label {
  font-weight: 600;
  color: #06284d;
  margin-bottom: 0.35rem;
}
.ledger-opening-card {
  border-bottom: 1px solid #eee;
  padding: 0.85rem 0.15rem;
}
.ledger-opening-card.is-replay {
  background: #eef8f1;
  border-radius: 8px;
  padding-left: 0.6rem;
  padding-right: 0.6rem;
}
</style>
